import { Injectable, OnDestroy, signal } from '@angular/core';
import { from, of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';

import {
  PatientBehaviorDataModel,
  PatientSessionModel,
  PatientTargetDataModel,
} from '../api/models';
import { StaffApiService } from '../api/services/staff-api.service';
import { OfflineDataService } from './offline-data.service';
import { SyncSchedulerService } from './sync-scheduler.service';
import { PatientSessionService } from '../../staff/home/patient-session.service';

@Injectable({
  providedIn: 'root',
})
export class SessionCollaborationSyncService implements OnDestroy {
  private readonly pollIntervalMs = 5000;
  private patientId: number | null = null;
  private sessionId: number | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private isPolling = false;

  readonly isRunning = signal<boolean>(false);

  constructor(
    private staffApiService: StaffApiService,
    private offlineDataService: OfflineDataService,
    private syncSchedulerService: SyncSchedulerService,
    private patientSessionService: PatientSessionService
  ) {}

  ngOnDestroy(): void {
    this.stop();
  }

  start(patientId: number, sessionId: number): void {
    if (!patientId || !sessionId || this.offlineDataService.isInOfflineMode()) {
      return;
    }

    this.patientId = patientId;
    this.sessionId = sessionId;
    this.isRunning.set(true);
    this.clearTimer();
    this.pollOnce();
  }

  stop(): void {
    this.clearTimer();
    this.patientId = null;
    this.sessionId = null;
    this.isPolling = false;
    this.isRunning.set(false);
  }

  mergeServerSession(
    localSession: PatientSessionModel | null | undefined,
    serverSession: PatientSessionModel
  ): { session: PatientSessionModel; hasPreservedLocalChanges: boolean } {
    const targetMerge = this.mergeDataCollection<PatientTargetDataModel>(
      localSession?.TargetData,
      serverSession.TargetData,
      'PatientTargetID'
    );
    const behaviorMerge = this.mergeDataCollection<PatientBehaviorDataModel>(
      localSession?.BehaviorData,
      serverSession.BehaviorData,
      'PatientBehaviorID'
    );

    return {
      session: {
        ...serverSession,
        TargetData: targetMerge.items,
        BehaviorData: behaviorMerge.items,
      },
      hasPreservedLocalChanges:
        targetMerge.hasPreservedLocalChanges ||
        behaviorMerge.hasPreservedLocalChanges,
    };
  }

  private mergeDataCollection<
    T extends { ID?: number | null; RecordedDate?: string | null }
  >(
    localItems: T[] | null | undefined,
    serverItems: T[] | null | undefined,
    ownerKey: keyof T
  ): { items: T[]; hasPreservedLocalChanges: boolean } {
    const mergedItems = [...(serverItems ?? [])];
    const serverIds = new Set(
      mergedItems
        .map((item) => item.ID)
        .filter((id): id is number => !!id && id > 0)
    );
    const serverNaturalKeys = new Set(
      mergedItems.map((item) => this.getNaturalKey(item, ownerKey))
    );
    let hasPreservedLocalChanges = false;

    for (const localItem of localItems ?? []) {
      if (localItem.ID && localItem.ID > 0) {
        continue;
      }

      const naturalKey = this.getNaturalKey(localItem, ownerKey);
      if (
        (localItem.ID && serverIds.has(localItem.ID)) ||
        serverNaturalKeys.has(naturalKey)
      ) {
        continue;
      }

      mergedItems.push(localItem);
      hasPreservedLocalChanges = true;
    }

    return { items: mergedItems, hasPreservedLocalChanges };
  }

  private getNaturalKey<T extends { RecordedDate?: string | null }>(
    item: T,
    ownerKey: keyof T
  ): string {
    const ownerId = item[ownerKey] ?? '';
    const recordedDate = item.RecordedDate
      ? item.RecordedDate.toString().slice(0, 19)
      : '';
    return `${String(ownerKey)}:${ownerId}:${recordedDate}`;
  }

  private pollOnce(): void {
    if (
      !this.isRunning() ||
      this.isPolling ||
      !this.patientId ||
      !this.sessionId
    ) {
      return;
    }

    const patientId = this.patientId;
    const sessionId = this.sessionId;
    this.isPolling = true;

    this.flushPendingLocalChanges(patientId)
      .pipe(
        switchMap((canFetchServer) => {
          if (!canFetchServer) {
            return of(null);
          }

          return this.staffApiService.GetPatientSessionWithData({
            sessionID: sessionId,
          });
        }),
        switchMap((serverSession) => {
          if (!serverSession) {
            return of(false);
          }

          const localSession =
            this.offlineDataService
              .getOfflinePatientData(patientId)
              ?.sessions.find((session) => session.ID === sessionId) ?? null;
          const mergeResult = this.mergeServerSession(
            localSession,
            serverSession
          );

          return from(
            this.offlineDataService.updateOfflineSessionFromServer(
              patientId,
              mergeResult.session,
              mergeResult.hasPreservedLocalChanges
            )
          ).pipe(
            tap((success) => {
              if (success) {
                this.patientSessionService.dataChanged.update(
                  (value) => value + 1
                );
              }
            })
          );
        }),
        catchError((error) => {
          console.warn('2:1 session sync poll failed', error);
          return of(false);
        }),
        finalize(() => {
          this.isPolling = false;
          if (this.isRunning()) {
            this.pollTimer = setTimeout(
              () => this.pollOnce(),
              this.pollIntervalMs
            );
          }
        })
      )
      .subscribe();
  }

  private flushPendingLocalChanges(patientId: number) {
    const hasPendingChanges =
      this.syncSchedulerService.hasPendingSync() ||
      this.offlineDataService.hasUnsyncedPatientData(patientId);

    if (!hasPendingChanges) {
      return of(true);
    }

    return this.syncSchedulerService.flushNow('2:1 session sync').pipe(
      catchError((error) => {
        console.warn('2:1 session sync could not flush local changes', error);
        return of(false);
      })
    );
  }

  private clearTimer(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
